import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import cn from 'classnames';

import {
  Header,
  Breadcrumb,
  Toggle,
  Paper,
  PaperContainer,
  Button,
  ButtonDownload,
} from '../../components';

import ClientInformation from './client.information';
import VirtualWallet from './virtual.wallet';
import ClientMarketing from './client.marketing';
import DiscountPlans from './discount.plans';
import ClientDiscounts from './client.discounts';
import ClientOrders from './client.orders';

import { clientsActions } from '../../state/clients/clients.actions';
import { actions } from '../../state';
import { config } from '../../api/config';
import { formatFilename, isEmpty } from '../../utils';

import styles from './clients.module.scss';

const EditClient = ({
  dispatch,
  user,
  match: { params },
  clients: { loading, selectedClient, clientInnerExport },
  xlsLoading,
}) => {
  const [status, setStatus] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(null);

  const fetchClient = useCallback(
    id => {
      dispatch(actions.clients.getClientAction(id));
    },
    [dispatch]
  );

  const clearClient = useCallback(() => {
    dispatch(actions.clients.clearClientsAction());
  }, [dispatch]);

  const loadValues = useCallback(() => {
    if (!isEmpty(selectedClient)) {
      const { status } = selectedClient;

      setStatus(status === 1 ? true : false);
      setTopUpAmount(null);
    }
  }, [selectedClient]);

  useEffect(() => {
    fetchClient(params.id);
    return () => {
      clearClient();
    };
  }, [fetchClient, clearClient, params.id]);

  useEffect(() => {
    loadValues();
  }, [loadValues]);

  const toggleStatus = () => {
    const newStatus = status ? 0 : 1;
    setStatus(status => !status);

    dispatch(actions.clients.updateClient(params.id, { status: newStatus }));
  };

  const toggleCommunicationSettings = settings => {
    dispatch(
      actions.clients.updateClient(params.id, {
        communication_settings: settings,
      })
    );
  };

  const openDeleteClientModal = () => {
    const payload = {
      modalName: 'confirmModal',
      title: 'Ištrinti kliento duomenis',
      desc: 'Ar tikrai norite ištrinti kliento duomenis?',
      onConfirm: () => dispatch(clientsActions.deleteClientData(params.id)),
    };
    dispatch(actions.modals.clearModals());
    dispatch(actions.modals.setModalVisible(payload));
  };

  const handleTopUpAmountChange = ({ target: { value } }) => {
    setTopUpAmount(+value);
  };

  const openTopUpClientWalletModal = () => {
    const payload = {
      modalName: 'confirmModal',
      title: 'Papildyti vartotojo e-piniginę',
      desc: 'Ar tikrai norite papildyti e-piniginę nurodyta suma?',
      onConfirm: () =>
        dispatch(
          clientsActions.topUpWallet(+params.id, Math.round(topUpAmount * 100))
        ),
    };
    dispatch(actions.modals.clearModals());
    dispatch(actions.modals.setModalVisible(payload));
  };

  const exportVirtualBalanceHistory = () => {
    const filename = formatFilename('Virtualaus balanso istorija');
    dispatch(
      actions.clients.exportVirtualBalanceHistoryAsyncAction(
        params.id,
        filename
      )
    );
  };

  const handleSubmit = data => {
    const body = {
      ...data,
      membership_id: Number(data.membership_id),
      client_id: Number(params.id),
    };
    dispatch(actions.memberships.addMembershipOrderToClient(body));
  };

  const onAssignDiscountToClient = data => {
    const body = {
      ...data,
      client_id: Number(params.id),
    };
    dispatch(actions.discounts.assignDiscountToClient(body));
  };

  const clientInformation = useMemo(() => {
    if (!Object.values(selectedClient).length) return [];
    return [
      ...(selectedClient.email
        ? [{ title: 'El. paštas', value: selectedClient.email }]
        : []),
      { title: 'Telefonas', value: selectedClient.phone },
      ...(selectedClient.last_location
        ? [
            {
              title: 'Paskutinė plovykla',
              value: selectedClient.last_location.address,
            },
            {
              title: 'Paskutinis miestas',
              value: selectedClient.last_location.city,
            },
            {
              title: 'Paskutinis BOX’as',
              value: selectedClient.last_location.device || '',
            },
          ]
        : []),
      ...(selectedClient.current_payment_method
        ? [
            {
              title: 'Pasirinktas atsiskaitymo būdas',
              value: selectedClient.current_payment_method,
            },
          ]
        : []),
    ];
  }, [selectedClient]);

  return (
    <section>
      <Header>
        <div className={styles.leftHeaderItemsContainer}>
          <Breadcrumb
            title={config.PAGES.clients}
            subtitle={selectedClient.phone}
            baseUrl={config.APP_PATHS.clients}
          />
          <div className={styles.actions}>
            <ButtonDownload
              onClick={exportVirtualBalanceHistory}
              loading={xlsLoading}
            />
            <div className={styles.activityToggleWrapper}>
              <Toggle
                onChange={toggleStatus}
                checked={status}
                showMeta={false}
                disabled={loading}
              />
              <span className={styles.toggleLabel}>Vartotojas aktyvus</span>
            </div>
            <Button color="dim" onClick={openDeleteClientModal}>
              Ištrinti klientą
            </Button>
          </div>
        </div>
      </Header>

      <PaperContainer className={styles.paperContainer}>
        <Paper className={cn(styles.paperContent, styles.fullHeightCard)}>
          <ClientInformation clientInformation={clientInformation} />
        </Paper>
        <Paper className={styles.paperContent}>
          <VirtualWallet
            virtual_balance={selectedClient.virtual_balance || 0}
            payment_cards={selectedClient.payment_cards || []}
            business_clients={selectedClient.business_clients || []}
            topUpAmount={topUpAmount}
            topUpClientWallet={openTopUpClientWalletModal}
            onTopUpAmountChanged={handleTopUpAmountChange}
          />
        </Paper>
        {selectedClient.communication_settings && (
          <Paper
            className={cn(styles.paperContent, styles.contentWithFullWidth)}
          >
            <ClientMarketing
              toggleCommunicationSettings={toggleCommunicationSettings}
              communication_settings={
                selectedClient.communication_settings || []
              }
            />
          </Paper>
        )}
        <Paper className={styles.paperContent}>
          <DiscountPlans
            onSubmit={handleSubmit}
            client_memberships={selectedClient.memberships || []}
          />
        </Paper>
        <Paper className={styles.paperContent}>
          <ClientDiscounts
            onAssignDiscountToClient={onAssignDiscountToClient}
            client_discounts={selectedClient.discounts || []}
          />
        </Paper>
        {params.id && (
          <Paper
            className={cn(styles.paperContent, styles.contentWithFullWidth)}
          >
            <ClientOrders client_id={Number(params.id)} />
          </Paper>
        )}
      </PaperContainer>
    </section>
  );
};

const mapStateToProps = state => ({
  user: state.auth.user,
  clients: state.clients,
  xlsLoading: state.download.xlsLoading,
});

EditClient.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  clients: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    selectedClient: PropTypes.object.isRequired,
  }).isRequired,
  xlsLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(EditClient);
